import React from 'react';
import {Text, View, PixelRatio} from 'react-native';
import {Props} from './model';
import {useStyles} from '../../../../shared/hooks/styles';
import {DeviceSize} from '../../../../shared/theme/helpers';
import {LoadableContent} from '../../../../shared/components/loadableContent';
import {format} from 'date-fns';
import {dateFormats} from '../../../../shared/constants/date';

export default ({cardInfoNew, isLoaded}: Props) => {
  const styles = useStyles(({theme, isDesktop, mediaOnly}) => ({
    container: {
      backgroundColor: theme.color.blue100,
      ...mediaOnly(
        DeviceSize.desktop,
        {
          paddingTop: 30,
          paddingBottom: 36,
          paddingHorizontal: 54,
          borderRadius: 40,
        },
        {
          paddingVertical: 20,
          paddingHorizontal: 16,
          borderRadius: 24,
        },
      ),
    },
    list: {
      flexDirection: isDesktop ? 'row' : 'column',
      flexWrap: 'wrap',
      gap: 'calc(1em + 16%)',
    },
    listItem: {
      flexDirection: isDesktop ? 'column' : 'row',
      ...mediaOnly(
        DeviceSize.desktop,
        {
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: '20%',
        },
        {
          marginBottom: 20,
        },
      ),
    },
    listItemLast: {
      ...mediaOnly(
        DeviceSize.desktop,
        {
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: '100%',
          marginRight: 0,
        },
        {
          marginBottom: 0,
        },
      ),
    },
    listTitleText: {
      ...(isDesktop ? theme.fonts.body : {bodySmallLight: theme.fonts.bodySmallLight, whiteSpace: 'break-spaces'}),
      color: theme.color.gray500,
    },
    listItemTitle: {
      ...mediaOnly(
        DeviceSize.desktop,
        {
          marginBottom: 4,
        },
        {
          flexBasis: 94 * PixelRatio.getFontScale(),
          minWidth: 94 * PixelRatio.getFontScale(),
          marginRight: 20,
        },
      ),
    },
    listItemValue: {
      ...mediaOnly(
        DeviceSize.desktop,
        {},
        {
          flex: 1,
        },
      ),
    },
    listValueText: {
      ...(isDesktop
        ? {
            ...theme.fonts.h6Light,
            marginTop: 5,
          }
        : theme.fonts.body),
      color: theme.color.textColor,
    },
    listValueTextBold: {
      ...(isDesktop
        ? {
            ...theme.fonts.h5,
            marginBottom: 10,
          }
        : {
            ...theme.fonts.action,
            marginBottom: 1,
          }),
      color: theme.color.textColor,
    },
    listValueTextSmall: {
      ...(isDesktop ? theme.fonts.body : theme.fonts.bodySmallLight),
      color: theme.color.labelText,
    },
  }));

  if (isLoaded && !cardInfoNew) {
    return null;
  }

  return (
    <LoadableContent loading={!isLoaded} style={styles.container}>
      <View style={styles.list}>
        {cardInfoNew?.number && (
          <View style={styles.listItem}>
            <View style={styles.listItemTitle}>
              <Text testID='card-info-id' style={styles.listTitleText} numberOfLines={1}>
                Карта:
              </Text>
            </View>
            <View style={styles.listItemValue}>
              <Text testID='card-number-id' style={styles.listValueTextBold} numberOfLines={1}>
                {cardInfoNew?.number}
              </Text>
              {cardInfoNew?.validTo && (
                <Text testID='card-state-id' style={styles.listValueTextSmall}>
                  {cardInfoNew?.validTo}
                </Text>
              )}
            </View>
          </View>
        )}
        {cardInfoNew?.balanceInRubles != null && (
          <View style={styles.listItem}>
            <View style={styles.listItemTitle}>
              <Text testID='card-balance-id' style={styles.listTitleText} numberOfLines={1}>
                Баланс:
              </Text>
            </View>
            <View style={styles.listItemValue}>
              <Text testID='card-balance-value-id' style={styles.listValueTextBold}>
                {cardInfoNew?.balanceInRubles}
                {typeof cardInfoNew?.balanceInRubles === 'number' ? ' ₽' : ''}
              </Text>
              <Text testID='card-state-id' style={styles.listValueTextSmall}>
                на {format(new Date(), dateFormats.fullDate)}
              </Text>
            </View>
          </View>
        )}
        {cardInfoNew?.cardName && (
          <View style={styles.listItem}>
            <View style={styles.listItemTitle}>
              <Text testID='card-type-id' style={styles.listTitleText} numberOfLines={1}>
                Тип карты:
              </Text>
            </View>
            <View style={styles.listItemValue}>
              <Text testID='card-type-value-id' style={styles.listValueTextBold}>
                {cardInfoNew?.cardName}
              </Text>
            </View>
          </View>
        )}
        {cardInfoNew?.tariffName && (
          <View style={styles.listItem}>
            <View style={styles.listItemTitle}>
              <Text testID='card-tarif-id' style={styles.listTitleText} numberOfLines={1}>
                Тариф:
              </Text>
            </View>
            <View style={styles.listItemValue}>
              <Text testID='card-tarif-value-id' style={styles.listValueTextBold}>
                {cardInfoNew?.tariffName}
              </Text>
            </View>
          </View>
        )}
        {cardInfoNew?.unwrittenPayment && (
          <View style={[styles.listItem, styles.listItemLast]}>
            <View style={styles.listItemTitle}>
              <Text testID='card-unwritten-payment-id' style={styles.listTitleText} numberOfLines={1}>
                Статус платежа:
              </Text>
            </View>
            <View style={styles.listItemValue}>
              <Text testID='card-unwritten-payment-value-id' style={styles.listValueTextBold}>
                {cardInfoNew?.unwrittenPayment}
              </Text>
            </View>
          </View>
        )}
      </View>
    </LoadableContent>
  );
};
